import { useState, useEffect } from "react";
import { StripeCardForm } from "./StripeCardForm";
import { useStudio } from "../studios/StudioContext";

// Prices come from the studio's server (GET /passes/catalog); the ids are fixed across studios.
const PASS_IDS       = ["dropin", "pass4", "pass8"];
const MEMBERSHIP_IDS = ["member2x", "memberUnl"];

const isMembershipType = (id) => MEMBERSHIP_IDS.includes(id);

export function PassPurchaseModal({ onClose, prefillName = "", prefillEmail = "", prefillPassType = "pass4", onPurchaseComplete }) {
  const { apiBaseUrl: API } = useStudio();
  const initialMode     = isMembershipType(prefillPassType) ? "membership" : "pass";
  const initialSelected = initialMode === "membership"
    ? (isMembershipType(prefillPassType) ? prefillPassType : "member2x")
    : (PASS_IDS.includes(prefillPassType) ? prefillPassType : "pass4");

  const [mode, setMode]                             = useState(initialMode);
  const [selected, setSelected]                     = useState(initialSelected);
  const [name, setName]                             = useState(prefillName);
  const [email, setEmail]                           = useState(prefillEmail);
  const [error, setError]                           = useState(null);
  const [purchased, setPurchased]                   = useState(null);
  const [subscribed, setSubscribed]                 = useState(null);
  const [memberClientSecret, setMemberClientSecret] = useState(null);
  const [fetchingSecret, setFetchingSecret]         = useState(false);
  const [couponCode, setCouponCode]                 = useState("");
  const [couponDiscount, setCouponDiscount]         = useState(null);
  const [couponError, setCouponError]               = useState(null);
  const [catalog, setCatalog]                       = useState(null);
  const [catalogError, setCatalogError]             = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`${API}/passes/catalog`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => { if (live) setCatalog(d); })
      .catch(() => { if (live) setCatalogError(true); });
    return () => { live = false; };
  }, [API]);

  const options      = (mode === "membership" ? catalog?.memberships : catalog?.passes) || [];
  const chosenPass   = options.find(p => p.id === selected) || options[0];
  const isMembership = mode === "membership";
  const nameValid    = name.trim().length >= 2;
  const emailValid   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleMembershipSetup = async () => {
    if (!nameValid || !emailValid) { setError("Please enter your full name and a valid email."); return; }
    setFetchingSecret(true);
    setError(null);
    try {
      const res  = await fetch(`${API}/passes/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), passType: selected }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Subscription setup failed.");
      setMemberClientSecret(data.clientSecret);
    } catch (err) {
      setError(err.message);
    } finally {
      setFetchingSecret(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponError(null);
    try {
      const res  = await fetch(`${API}/coupons/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: couponCode.trim(), passType: selected, email: (email || "").trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) { setCouponError(data.error ?? "Invalid code."); return; }
      setCouponDiscount(data.discount);
    } catch { setCouponError("Something went wrong."); }
  };


  const handlePassSuccess = async (paymentIntentId) => {
    try {
      const res  = await fetch(`${API}/passes/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(), email: email.trim(),
          passType: selected, stripePaymentId: paymentIntentId,
          couponCode: couponDiscount ? couponDiscount.code : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Purchase failed.");
      setPurchased(data.pass);
      onPurchaseComplete?.(data.pass.code);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMemberSuccess = () => {
    setSubscribed({
      type: selected, label: chosenPass.label,
      price: chosenPass.display,
      actualCharge: chosenPass.actualCharge ?? chosenPass.display,
      email: email.trim(),
    });
   let attempts = 0;
  const poll = setInterval(async () => {
    attempts++;
    try {
      const res = await fetch(`${API}/passes/active?email=${encodeURIComponent(email.trim())}`);
      const data = await res.json();
      if (data.pass?.code) {
        clearInterval(poll);
        onPurchaseComplete?.(data.pass.code);
      } else if (attempts >= 7) {
        clearInterval(poll);
        onPurchaseComplete?.(null); // fallback — let student enter code manually
      }
    } catch {
      clearInterval(poll);
      onPurchaseComplete?.(null);
    }
  }, 1500);
};

  if (purchased) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <div className="booking-success">
            <div className="booking-success-icon">✓</div>
            <h2 className="booking-success-title">Pass confirmed!</h2>
            <p className="booking-success-sub">Your pass code has been emailed to you.</p>
            <div className="pass-purchase-code">{purchased.code}</div>
            <p className="booking-success-sub" style={{fontSize:"0.85rem", opacity:0.7}}>
              {purchased.classesTotal} class{purchased.classesTotal !== 1 ? "es" : ""}
              {purchased.expiresAt
                ? ` · expires ${new Date(purchased.expiresAt).toLocaleDateString("en-US", {month:"long", day:"numeric", year:"numeric"})}`
                : ""}
            </p>
            <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <p className="booking-success-sub" style={{padding:"2rem 0", textAlign:"center"}}>
            {catalogError ? "Prices couldn't be loaded. Please try again in a moment." : "Loading prices…"}
          </p>
        </div>
      </div>
    );
  }

  if (subscribed) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <div className="booking-success">
            <div className="booking-success-icon">✓</div>
            <h2 className="booking-success-title">You're a member!</h2>
            <p className="booking-success-sub">
              Welcome to the {subscribed.label}. Your member code and welcome details
              are on their way to <strong>{subscribed.email}</strong>.
            </p>
            <p className="booking-success-sub" style={{fontSize:"0.85rem", opacity:0.7, marginTop:"0.75rem"}}>
              Your card will be charged ${subscribed.actualCharge.toFixed(2)} automatically each month
              {subscribed.actualCharge > subscribed.price ? " (includes card processing fee)" : ""} until you cancel. Manage your membership anytime
              from <strong>My Bookings</strong>.
            </p>
            <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h2 className="modal-title">{isMembership ? "Become a Member" : "Buy a Pass"}</h2>

        <div className="pay-mode-tabs" style={{marginBottom:"1rem"}}>
          <button type="button"
            className={`pay-mode-tab ${!isMembership ? "selected" : ""}`}
            onClick={() => { setMode("pass"); setSelected(PASS_IDS.includes(selected) ? selected : "pass4"); setError(null); setMemberClientSecret(null); }}>
            One-time pass
          </button>
          <button type="button"
            className={`pay-mode-tab ${isMembership ? "selected" : ""}`}
            onClick={() => { setMode("membership"); setSelected(isMembershipType(selected) ? selected : "member2x"); setError(null); setMemberClientSecret(null); }}>
            Monthly membership
          </button>
        </div>

        <div className="pass-purchase-options">
          {options.map(opt => (
            <div key={opt.id}
              className={`pass-purchase-option ${selected === opt.id ? "selected" : ""}`}
              onClick={() => { setSelected(opt.id); setMemberClientSecret(null); }}>
              <div className="pass-purchase-option-left">
                <span className="pass-purchase-option-label">{opt.label}</span>
                <span className="pass-purchase-option-sub">
                  {isMembership ? opt.sub : `${opt.classes} class${opt.classes !== 1 ? "es" : ""}`}
                </span>
              </div>
              <span className="pass-purchase-option-price">
                ${opt.display}{isMembership ? <small style={{fontWeight:400, opacity:0.7}}> /mo</small> : null}
              </span>
              {selected === opt.id && <span className="booking-option-check">✓</span>}
            </div>
          ))}
        </div>

        <div className="booking-fields" style={{margin:"1.25rem 0"}}>
          <label className="form-label">Full Name
            <input className="form-input" value={name} onChange={e => { setName(e.target.value); setMemberClientSecret(null); }}
              placeholder="e.g. Jane Smith" minLength={2} required />
          </label>
          <label className="form-label">Email
            <input className="form-input" type="email" value={email}
              onChange={e => { setEmail(e.target.value); setMemberClientSecret(null); }} placeholder="your@email.com" />
          </label>
        </div>

        {error && <p className="pass-feedback invalid" style={{marginTop:"0.75rem"}}>{error}</p>}
          <div className="em-coupon-row" style={{marginBottom:"0.5rem"}}>
              <input
                className="em-input"
                placeholder="Discount code"
                value={couponCode}
                onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponDiscount(null); setCouponError(null); }}
              />
              <button className="stripe-pass-link" onClick={handleApplyCoupon}>Apply</button>
            </div>
            {couponDiscount && (
              <p className="pass-feedback valid" style={{marginBottom:"0.5rem"}}>✓ {couponDiscount.displayText} applied</p>
            )}
            {couponError && (
              <p className="pass-feedback invalid" style={{marginBottom:"0.5rem"}}>{couponError}</p>
            )}

        {!isMembership && (
          <StripeCardForm
             amount={couponDiscount
              ? Math.max(50, Math.round(
                  couponDiscount.type === "percent"
                    ? chosenPass.stripeAmount * (1 - couponDiscount.amount / 100)
                    : chosenPass.stripeAmount - (couponDiscount.amount * 100)
                ))
              : chosenPass.stripeAmount}
            email={email.trim()}
            billingDetails={{ name: name.trim(), email: email.trim() }}
            onSuccess={handlePassSuccess}
            onError={(msg) => setError(msg)}
            submitLabel={couponDiscount
              ? `Pay $${(couponDiscount.type === "percent"
                  ? chosenPass.display * (1 - couponDiscount.amount / 100)
                  : Math.max(0, chosenPass.display - couponDiscount.amount)
                ).toFixed(2)} & Get Pass`
              : `Pay $${chosenPass.display} & Get Pass`}
            disabled={!nameValid || !emailValid}
          />
        )}

        {isMembership && !memberClientSecret && (
          <button className="book-btn" style={{marginTop:"1.25rem"}}
            disabled={!nameValid || !emailValid || fetchingSecret}
            onClick={handleMembershipSetup}>
            {fetchingSecret ? "Setting up…" : `Subscribe — $${chosenPass.display}/month`}
          </button>
        )}

        {isMembership && memberClientSecret && (
          <StripeCardForm
            clientSecret={memberClientSecret}
            email={email.trim()}
            billingDetails={{ name: name.trim(), email: email.trim() }}
            onSuccess={handleMemberSuccess}
            onError={(msg) => { setError(msg); setMemberClientSecret(null); }}
            submitLabel={`Confirm — $${chosenPass.display}/month`}
          />
        )}

        <p style={{marginTop:"0.75rem", fontSize:"0.8rem", color:"var(--color-grey-400)", textAlign:"center", lineHeight:1.5}}>
          {isMembership
            ? <>Your card will be charged <strong>${(chosenPass.actualCharge ?? chosenPass.display).toFixed(2)}</strong>/month
                {chosenPass.actualCharge > chosenPass.display ? " (includes card processing fee)" : ""} on the same day each month. Cancel anytime from My Bookings.</>
            : `${chosenPass.stripeAmount > Math.round(chosenPass.display * 100) ? "Price includes card processing fee. " : ""}Your pass code will be emailed immediately.`
          }
        </p>
      </div>
    </div>
  );
}
